import {
  IStringConditionObject,
  IValue,
  IUIUrval,
  UIUrvalConditionObjectType,
  IUrvalCondition,
  IChecklistConditionObject,
  INumericConditionObject,
  ValueConditionObjectType,
  IBooleanConditionObject,
} from '../../../../../../../../../../../../../Types/UrvalTypes';
import { IRanges } from '../EkonomiskAnalysSimpleUrval';
import { assocPath, append, update, uniqWith } from 'ramda';
import shortid from 'shortid';
import { IClientAttributeModel } from '../../../../../../../../../../../../../Types/ClientModelTypes';
import { getEmptyUrval } from '../../../../../../../../../../../../../Components/Urval/Utils/urvalUtils';

import { getChecklistValuesAndLabelsWithCode } from '../../../../../../../../../../../../../Components/Urval/Utils/urvalUtils';
import { TEDSelectionOperator } from '../../../../../../../../../../../../../globalQueryTypes';
import Logger from '../../../../../../../../../../../../../Utils/Logger';
import {
  IKontodelar,
  IKontodelValue,
} from '../../../../../../../../Queries/EkonomiskAnalysKontodelarQuery/EkonomiskAnalysKontodelarQuery';
import { IKontoValues } from '../../../../../../../../Queries/EkonomiskAnalysKontobegreppQuery/EkonomiskAnalysKontobegreppQuery';
import { ISuggestion } from '../../../../../../../../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';

interface IConditionObjectInfo {
  conditionObject: UIUrvalConditionObjectType;
  isNewConditionObject: boolean;
}

interface ISimpleConditions {
  valueConditions: ValueConditionObjectType[];
  checklistConditions: IChecklistConditionObject[];
  booleanConditions: IBooleanConditionObject[];
}

export const rangesFromUIUrval = (urval: IUIUrval | null) => {
  // const stringConditions = getStringConditions(urval);
  // const checklistConditions = getChecklistConditions(urval);
  const simpleConditions = getSimpleConditions(urval);
  const urvalRanges: IRanges = {};

  simpleConditions.valueConditions.forEach(cond => {
    cond.condition[0].values.forEach(condValue => {
      if (urvalRanges[cond.attribute.AttributeName] == null) {
        urvalRanges[cond.attribute.AttributeName] = `${rangeFromConditionValue(
          condValue
        )}`;
      } else {
        urvalRanges[
          cond.attribute.AttributeName
        ] += `, ${rangeFromConditionValue(condValue)}`;
      }
    });
  });

  simpleConditions.checklistConditions.forEach(cond => {
    urvalRanges[cond.attribute.AttributeName] =
      getChecklistEnumValues(cond).join(',');
  });

  simpleConditions.booleanConditions.forEach(cond => {
    urvalRanges[cond.attribute.AttributeName] =
      getBooleanEnumValues(cond).join(',');
  });
  return urvalRanges;
};

const getBooleanEnumValues = (condition: IBooleanConditionObject): string[] => {
  const result: string[] = [];
  if (condition.condition.yes) {
    result.push('Ja');
  }
  if (condition.condition.no) {
    result.push('Nej');
  }

  return result;
};

const getChecklistEnumValues = (
  checklist: IChecklistConditionObject
): string[] => {
  const checkValues = getChecklistValuesAndLabelsWithCode(checklist.attribute);
  return checkValues
    .filter(c => checklist.condition.includes(c.value))
    .map(c => c.code || c.displayLabel);
};
const rangeFromConditionValue = (value: IValue) => {
  let result = '';
  if (value.value) {
    result += value.value;
  }
  if (value.toValue) {
    result += `-${value.toValue}`;
  }

  return result;
};

const getSimpleConditions = (urval: IUIUrval | null): ISimpleConditions => {
  let valueConditions: ValueConditionObjectType[] = [];
  let checklistConditions: IChecklistConditionObject[] = [];
  let booleanConditions: IBooleanConditionObject[] = [];
  if (urval && urval.pages[0] != null) {
    urval.pages[0].conditionObjects
      .filter(conditionObject => !conditionObject.hideInSimpleUrval)
      .forEach(conditionObject => {
        switch (conditionObject.type) {
          case 'String':
            valueConditions.push(conditionObject as IStringConditionObject);
            break;
          case 'Numeric':
            valueConditions.push(conditionObject as INumericConditionObject);
            break;
          case 'Checklist':
            checklistConditions.push(
              conditionObject as IChecklistConditionObject
            );
            break;
          case 'Boolean':
            booleanConditions.push(conditionObject as IBooleanConditionObject);
            break;
        }
      });
  }

  // TODO: Vad ska hända med enkelt urval när man har lagt till flera av samma fält i avancerat urval?
  const uniqueValueConditions = uniqWith(
    (a1, a2) => a1.attribute.AttributeName === a2.attribute.AttributeName,
    valueConditions
  );
  const uniqueChecklistConditions = uniqWith(
    (a1, a2) => a1.attribute.AttributeName === a2.attribute.AttributeName,
    checklistConditions
  );

  const uniqueBooleanConditions = uniqWith(
    (a1, a2) => a1.attribute.AttributeName === a2.attribute.AttributeName,
    booleanConditions
  );

  return {
    valueConditions: uniqueValueConditions,
    checklistConditions: uniqueChecklistConditions,
    booleanConditions: uniqueBooleanConditions,
  };
};

export const createConditionValues = (range: string, numeric: boolean) => {
  const ranges = range.split(',');
  let newConditionValues: IValue[] = [];
  ranges.forEach(range => {
    const newConditionValue = createConditionValue(range, numeric);
    newConditionValues = append(newConditionValue, newConditionValues);
  });

  return newConditionValues;
};

const createConditionValue = (
  currentRange: string,
  numeric: boolean
): IValue => {
  const values = currentRange ? currentRange.split('-') : undefined;
  let value;
  let toValue;

  if (values != null && values.length > 0) {
    if (values.length > 2) {
      value = undefined;
      toValue = undefined;
    } else if (values.length === 1) {
      value = numeric ? Number(values[0].trim()) : values[0].trim();
    } else if (values.length === 2) {
      value = numeric ? Number(values[0].trim()) : values[0].trim();
      toValue = numeric ? Number(values[1].trim()) : values[1].trim();
    }
  } else {
    value = undefined;
    toValue = undefined;
  }

  return {
    id: shortid.generate(),
    value: value,
    toValue: toValue,
  };
};

const createNewConditionObject = (
  attribute: IClientAttributeModel
): IStringConditionObject => {
  return {
    type: 'String',
    id: attribute.AttributeName,
    attribute: attribute,
    condition: [
      {
        id: shortid.generate(),
        op: TEDSelectionOperator.interval,
        values: [{ id: shortid.generate(), value: '', toValue: '' }],
      },
    ],
  };
};

const getConditionObjectInfo = (
  conditionObjects: UIUrvalConditionObjectType[],
  index: number,
  attributeName: string,
  kontoDelList: IClientAttributeModel[]
): IConditionObjectInfo | undefined => {
  const attribute = kontoDelList.find(x => x.AttributeName === attributeName);

  if (attribute == null) {
    return undefined;
  }

  if (index === -1) {
    return {
      conditionObject: createNewConditionObject(attribute),
      isNewConditionObject: true,
    };
  } else {
    const conditionObject = conditionObjects[index];
    if (conditionObject == null) {
      Logger.error('conditionObject is null');
      return undefined;
    }
    return { conditionObject: conditionObject, isNewConditionObject: false };
  }
};

export const updateUrval = (
  ranges: IRanges,
  urval: IUIUrval | null,
  kontoDelList: IClientAttributeModel[]
): IUIUrval => {
  if (urval == null || urval.pages[0] == null) {
    urval = getEmptyUrval();
  }

  let newConditionObjects: UIUrvalConditionObjectType[] =
    urval.pages[0].conditionObjects;

  const updateConditionObject = ([key, range]: [string, string]) => {
    const conditionObjectIndex = newConditionObjects.findIndex(
      conditionObject =>
        conditionObject.type !== 'Extended' &&
        conditionObject.attribute.AttributeName === key
    );

    const conditionObjectInfo = getConditionObjectInfo(
      newConditionObjects,
      conditionObjectIndex,
      key,
      kontoDelList
    );

    if (conditionObjectInfo == null) {
      Logger.error('Could not set urval');
      return;
    }

    const { conditionObject, isNewConditionObject } = conditionObjectInfo;

    const newConditions: IUrvalCondition[] = [
      {
        ...(conditionObject.condition as IUrvalCondition[])[0],
        values: createConditionValues(range, conditionObject.type === 'Numeric'),
        op: TEDSelectionOperator.interval,
      },
    ];

    if (conditionObject.type === 'Checklist') {
      const checkValues = getChecklistValuesAndLabelsWithCode(
        conditionObject.attribute
      );
      const rangeValues = range.trim().split(',');
      const newConditionObject = {
        ...conditionObject,
        condition: checkValues
          .filter(c =>
            rangeValues.find(r => {
              const rangeValue = c.code != null ? c.code : c.displayLabel;
              return r === rangeValue;
            })
          )
          .map(c => c.value),
      } as IChecklistConditionObject;

      if (isNewConditionObject) {
        newConditionObjects = append(newConditionObject, newConditionObjects);
      } else {
        newConditionObjects = update(
          conditionObjectIndex,
          newConditionObject,
          newConditionObjects
        );
      }
    } else if (conditionObject.type !== 'Extended') {
      const newConditionObject = {
        ...conditionObject,
        condition: newConditions,
      } as IStringConditionObject;

      if (isNewConditionObject) {
        newConditionObjects = append(newConditionObject, newConditionObjects);
      } else {
        newConditionObjects = update(
          conditionObjectIndex,
          newConditionObject,
          newConditionObjects
        );
      }
    }
  };

  Object.entries(ranges).forEach(updateConditionObject);

  const newUrval: IUIUrval = assocPath(
    ['pages', 0, 'conditionObjects'],
    newConditionObjects,
    urval
  );

  return newUrval;
};

const getEnumCodeLength = (kontodel: IClientAttributeModel) =>
  kontodel.AttributeName === 'KB_VERKSAMHET' ? 3 : 1;

export const getKontodelarCodeLength = (
  kontodelar: IKontodelar,
  kontodel: IClientAttributeModel,
  kontobegreppValues: IKontoValues | undefined
) => {
  const kontodelarEntry = kontodelar[kontodel.AttributeName];
  return kontodelarEntry
    ? kontodelarEntry.codeLength
    : kontodel.DataType === 'Enum'
    ? getEnumCodeLength(kontodel)
    : kontobegreppValues
    ? kontobegreppValues.values.codeLength
    : undefined;
};

export const kontodelarToSuggestions = (
  kontodelar: IKontodelar,
  kontodel: IClientAttributeModel,
  kontobegreppValues: IKontoValues | undefined
): ISuggestion[] => {
  const enumValues = getKontodelarEnumValues(kontodel);
  const kontodelarEntry = kontodelar[kontodel.AttributeName];
  return kontodelarEntry
    ? kontodelarEntry.kontodelar.map(
        (x: IKontodelValue) => ({ name: x.name, value: x.id })
      )
    : kontodel.DataType === 'Enum'
    ? enumValues
    : kontobegreppValues
    ? kontobegreppValues.values.kontodelar.map(x => ({
        name: x.name,
        value: x.id,
      }))
    : [];
};

export interface IEnumSuggestion extends ISuggestion {
  data?: {
    code: string;
  };
}

export const getEnumCode = (displayLabel: string): string | undefined => {
  const digitsRegexMatch = displayLabel.match(/(\d*) /g);

  const digits =
    digitsRegexMatch != null ? digitsRegexMatch[0].trim() : undefined;

  return digits;
};

const getKontodelarEnumValues = (
  kontodel: IClientAttributeModel
): IEnumSuggestion[] => {
  return getChecklistValuesAndLabelsWithCode(kontodel)
    .filter(value => value.value)
    .map(value => {
      return {
        name: value.displayLabel,
        value: value.value,
        data: value.code != null ? { code: value.code } : undefined,
      };
    });
};
